import { Injectable } from '@angular/core';

type PairType = 'PUBLIC' | 'PRIVATE';
export const PackageAlgorithm = 'AES-GCM';
export const PackageAlgorithmLength = 256;
export const PairAlgorithm = 'RSA-OAEP';
export const PairKeyLength = 4096;
export const PairHash = 'SHA-256';

export class CryptoException extends Error {
    constructor(public type: 'malformed_key' | 'invalid_key', public inner: any) {
        super(type, inner);
    }
}

export interface EncryptedPackage {
    secret: string,
    data: ArrayBuffer
}

@Injectable({
    providedIn: 'root'
})
export class CryptoService {
    constructor() { }

    /**
     * Packages data to an encrypted data blob. Also returns
     * an encrypted secret which together with the private key
     * is used to decrypt the package.
     * @param data data to package
     * @param key public key used for packaging
     * @returns the encrypted secret and data
     */
    async package(data: Uint8Array, key: string): Promise<EncryptedPackage> {
        // key used to encrypt the files
        const secret = await crypto.subtle.generateKey(
            {
                name: PackageAlgorithm,
                length: PackageAlgorithmLength,
            },
            true,
            ["encrypt"]
        );

        let publicKey: CryptoKey;
        try {
            publicKey = await this.importPairKey(key, 'PUBLIC')
        }
        catch (err) {
            throw new CryptoException('malformed_key', err);
        }

        const { iv, encrypted } = await this.encrypt(data, secret);
        const wrappedKey = await crypto.subtle.wrapKey(
            'raw',
            secret,
            publicKey,
            PairAlgorithm);

        return {
            secret: `${this.btoa(iv)}_${this.btoa(wrappedKey)}`,
            data: encrypted
        }
    }

    async checksum(data: BufferSource): Promise<string> {
        const hashBuffer = await crypto.subtle.digest('sha-256', data);
        const hashArray = [...new Uint8Array(hashBuffer)];

        const hash = hashArray
            .map((item) => item.toString(16).padStart(2, "0"))
            .join("");
        return hash;
    }

    /***
     * Encodes an array buffer to a base64-encoded string
     */
    private btoa(array: ArrayBuffer): string {
        const exportedAsString = String.fromCharCode.apply(null, new Uint8Array(array) as any);
        return btoa(exportedAsString);
    }

    async importPairKey(value: string, type: PairType): Promise<CryptoKey> {
        const keyUsages: { [T in PairType]: KeyUsage[] } = {
            'PUBLIC': ['wrapKey'],
            'PRIVATE': ['unwrapKey']
        };

        return crypto.subtle.importKey(
            'jwk',
            JSON.parse(value),
            {
                name: PairAlgorithm,
                hash: PairHash
            },
            false,
            keyUsages[type]);
    }

    private async encrypt(data: Uint8Array, key: CryptoKey) {
        // initialization vector
        const iv = crypto.getRandomValues(new Uint8Array(12));

        return {
            iv,
            encrypted: await crypto.subtle.encrypt({
                name: 'AES-GCM',
                iv
            }, key, data)
        };
    }
}
